import { useState } from 'react';

import { api } from '../../api';
import { go } from '../../App';
import { useLoad, usePolled } from '../../data';
import { InkHero, Loading, MoneyText, PageHead, Panel, QCard, StatusChip, ago, fmtMoney, formatWhen, useUndoToast } from '../../ui';
import { Booking, DECLINE_REASONS } from '../../types';

// H3 — Declined and alternatives. A queue, not a filter on a table: every
// card here is a guest who is still waiting to be told something. The
// compare view is the Split grid's reason to exist — the counter-offer
// beside what was asked, differences highlighted. An expired request — the
// supplier never answered and the system closed it (no_answer) — lands
// here too: V2's "the hotel is told plainly, in time to reroute".

// What goes wrong soonest: for an alternative it is the hold lapsing or the
// start arriving, whichever is first; for a decline it is the start itself.
function urgencyAt(b: Booking): number {
  const start = Date.parse(b.start);
  if (b.status === 'alternative' && b.holdExpiresAt) {
    return Math.min(Date.parse(b.holdExpiresAt), start);
  }
  return start;
}

function reasonLabel(code?: string): string {
  return DECLINE_REASONS.find((r) => r.code === code)?.label ?? 'Declined';
}

function expiredNoAnswer(b: Booking): boolean {
  return b.status === 'cancelled' && b.cancelReason === 'no_answer';
}

function holdChip(b: Booking): { cls: string; text: string } {
  if (b.holdExpiresAt) {
    const ms = Date.parse(b.holdExpiresAt) - Date.now();
    if (ms <= 0) return { cls: 'soon', text: 'Hold lapsed' };
    const h = Math.max(1, Math.round(ms / 3600_000));
    return { cls: 'waiting', text: `New time · held ${h} h more` };
  }
  return { cls: 'waiting', text: 'New time offered' };
}

export function Alternatives({ openId }: { openId?: string }) {
  const { data, error, reload } = usePolled<{ items: Booking[] }>('/bookings');
  const toast = useUndoToast(reload);
  const items = data ? data.items : null;

  const confirmed = (booking: Booking) => {
    toast.show(
      booking,
      `New time confirmed · ${booking.productName}`,
      `${booking.supplierName} has been told.`,
    );
    go('/alternatives');
    reload();
  };

  const queue = (items ?? [])
    .filter(
      (b) =>
        b.status === 'alternative' ||
        ((b.status === 'declined' || expiredNoAnswer(b)) && Date.parse(b.start) > Date.now()),
    )
    .sort((a, b) => urgencyAt(a) - urgencyAt(b));

  if (openId) {
    return (
      <>
        <Compare id={openId} onConfirmed={confirmed} />
        {toast.element}
      </>
    );
  }

  const count = queue.length;
  return (
    <div>
      <InkHero
        eyebrow="Declined and alternatives"
        figure={items === null ? '…' : count}
        unit={count === 1 ? 'guest needs a decision' : 'guests need a decision'}
        explain="Ordered by what goes wrong soonest — not by when it arrived."
      />

      {error && items === null && <p className="error-line">{error}</p>}
      {items !== null && count === 0 ? (
        <div className="empty">
          <strong>Nothing came back</strong>
          <p>Every request was answered as you asked. Declines and counter-offers would land here.</p>
        </div>
      ) : (
        queue.map((b, i) => <QueueCard key={b.id} b={b} escalated={i === 0 && urgencyAt(b) - Date.now() < 2 * 3600_000} />)
      )}

      {toast.element}
    </div>
  );
}

function QueueCard({ b, escalated }: { b: Booking; escalated: boolean }) {
  const alt = b.status === 'alternative';
  const expired = expiredNoAnswer(b);
  return (
    <QCard
      chip={
        alt ? (
          <span className={`uchip ${holdChip(b).cls}`}>{holdChip(b).text}</span>
        ) : expired ? (
          // The ladder's terminal state wears the urgency clay — the status
          // chip alone would read as an ordinary cancellation.
          <span className="uchip escalated">No answer by the deadline</span>
        ) : (
          // A status wears its own colour, everywhere — declined is rust,
          // never a clay urgency chip.
          <StatusChip status="declined" />
        )
      }
      title={b.productName ?? b.productId}
      meta={
        alt
          ? `${b.supplierName} · ${b.guestName}${b.guestRoom ? ` · ${b.guestRoom}` : ''}`
          : expired
            ? `${b.supplierName} didn’t answer — the request expired · ${b.guestName}${b.guestRoom ? ` · ${b.guestRoom}` : ''}`
            : `${reasonLabel(b.declineReason)} — ${b.supplierName} · ${b.guestName}${b.guestRoom ? ` · ${b.guestRoom}` : ''}`
      }
      amount={fmtMoney(b.guestAmount)}
      caption="guest pays"
      when={
        alt && b.proposedStart
          ? `${formatWhen(b.start)} → ${formatWhen(b.proposedStart)}`
          : formatWhen(b.start)
      }
      open={alt ? 'Compare →' : 'Decide →'}
      escalated={escalated}
      onClick={() => go(`/alternatives/${b.id}`)}
    />
  );
}

// "3 hours later" / "2 days earlier" — the one line that says what actually
// changes if the hotel says yes.
function shiftLabel(orig: string, proposed: string): string {
  const ms = Date.parse(proposed) - Date.parse(orig);
  const dir = ms > 0 ? 'later' : 'earlier';
  if (orig.slice(0, 10) === proposed.slice(0, 10)) {
    const h = Math.max(1, Math.round(Math.abs(ms) / 3600_000));
    return `the same day, ${h} ${h === 1 ? 'hour' : 'hours'} ${dir}`;
  }
  const d = Math.max(1, Math.round(Math.abs(ms) / 86_400_000));
  return `${d} ${d === 1 ? 'day' : 'days'} ${dir}`;
}

function Compare({
  id,
  onConfirmed,
}: {
  id: string;
  onConfirmed: (b: Booking) => void;
}) {
  const { data: booking, error: loadError } = useLoad<Booking>(`/bookings/${id}`);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      const after = await api<Booking>('POST', `/bookings/${id}/accept-alternative`);
      onConfirmed(after);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  if (!booking) {
    return loadError ? <p className="error-line">{loadError}</p> : <Loading />;
  }

  // A declined booking's decision view — H3's "reroute" has an address, so
  // a decline notification can deep-link straight to it.
  if (booking.status === 'declined') {
    const declineEntry = (booking.timeline ?? []).filter((t) => t.to === 'declined').slice(-1)[0];
    return (
      <div>
        <PageHead
          title={booking.productName ?? 'Booking'}
          sub={`${booking.supplierName} can’t do this one — the guest needs another plan.`}
        />
        <Panel title="What was asked" meta={<StatusChip status="declined" />}>
          <div className="kv">
            <div className="kvrow">
              <span className="k">When</span>
              <span className="v">{formatWhen(booking.start)}</span>
            </div>
            <div className="kvrow">
              <span className="k">Guest</span>
              <span className="v">
                {booking.guestName}
                {booking.guestRoom ? ` · ${booking.guestRoom}` : ''}
              </span>
            </div>
            <div className="kvrow">
              <span className="k">Party</span>
              <span className="v">
                {booking.guests.adults} adults{booking.guests.children ? ` · ${booking.guests.children} children` : ''}
              </span>
            </div>
            <div className="kvrow">
              <span className="k">Why not</span>
              <span className="v">{reasonLabel(booking.declineReason)}</span>
            </div>
          </div>
          {declineEntry?.note && <div className="said">&ldquo;{declineEntry.note}&rdquo;</div>}
          <p className="hint" style={{ margin: '10px 0 14px' }}>
            The reason is theirs, in their words — it is what lets you offer the guest something else.
          </p>
          <button className="btn" style={{ width: '100%' }} onClick={() => go('/bookings/new')}>
            Book another supplier
          </button>
        </Panel>
      </div>
    );
  }

  // An expired request's decision view — the supplier did not answer, the
  // system closed it at the deadline, and the guest still needs a plan.
  if (booking.status === 'cancelled' && booking.cancelReason === 'no_answer') {
    return (
      <div>
        <PageHead
          title={booking.productName ?? 'Booking'}
          sub={`${booking.supplierName} didn’t answer by the deadline — the guest needs another plan.`}
        />
        <Panel title="What was asked" meta={<span className="uchip escalated">No answer by the deadline</span>}>
          <div className="kv">
            <div className="kvrow">
              <span className="k">When</span>
              <span className="v">{formatWhen(booking.start)}</span>
            </div>
            <div className="kvrow">
              <span className="k">Guest</span>
              <span className="v">
                {booking.guestName}
                {booking.guestRoom ? ` · ${booking.guestRoom}` : ''}
              </span>
            </div>
            <div className="kvrow">
              <span className="k">Party</span>
              <span className="v">
                {booking.guests.adults} adults{booking.guests.children ? ` · ${booking.guests.children} children` : ''}
              </span>
            </div>
            <div className="kvrow">
              <span className="k">What happened</span>
              <span className="v">Chased, escalated, no answer — the request expired</span>
            </div>
          </div>
          <p className="hint" style={{ margin: '10px 0 14px' }}>
            The timeline on the booking shows every chase. Nobody declined — the clock ran out.
          </p>
          <button className="btn" style={{ width: '100%' }} onClick={() => go('/bookings/new')}>
            Book another supplier
          </button>
        </Panel>
      </div>
    );
  }

  if (booking.status !== 'alternative' || !booking.proposedStart) {
    return (
      <div>
        <PageHead title={booking.productName ?? 'Booking'} sub={`This one is settled — it is ${booking.status} now.`} />
        <a href={`#/bookings/${booking.id}`} className="open">
          Open the booking →
        </a>
      </div>
    );
  }

  const proposal = (booking.timeline ?? []).filter((t) => t.to === 'alternative').slice(-1)[0];
  const holdMs = booking.holdExpiresAt ? Date.parse(booking.holdExpiresAt) - Date.now() : 0;
  const party = `${booking.guests.adults} adults${booking.guests.children ? ` · ${booking.guests.children} children` : ''}`;

  return (
    <div>
      <PageHead
        title={booking.productName ?? 'Booking'}
        sub={`${booking.supplierName} can’t do the time you asked — they offer ${shiftLabel(booking.start, booking.proposedStart)}.`}
      />

      {holdMs <= 0 && (
        <p className="error-line" style={{ marginBottom: 20 }}>
          The 12-hour hold has lapsed — if the slot has gone, this returns to Requested for {booking.supplierName} to answer again.
        </p>
      )}

      <div className="split">
        <Panel title="What you asked for">
          <div className="kv">
            <div className="kvrow dim">
              <span className="k">When</span>
              <span className="v">{formatWhen(booking.start)}</span>
            </div>
            <div className="kvrow">
              <span className="k">Guest</span>
              <span className="v">
                {booking.guestName}
                {booking.guestRoom ? ` · ${booking.guestRoom}` : ''}
              </span>
            </div>
            <div className="kvrow">
              <span className="k">Party</span>
              <span className="v">{party}</span>
            </div>
            <div className="kvrow earn">
              <span className="k">Guest pays</span>
              <span className="v">
                <MoneyText money={booking.guestAmount} />
              </span>
            </div>
          </div>
          <p className="hint">
            Requested {ago(booking.createdAt)} ago. If the guest was already told this time, check with them before confirming the new one.
          </p>
        </Panel>

        <Panel
          title={`What ${booking.supplierName} offers`}
          meta={holdMs > 0 ? `held ${Math.max(1, Math.round(holdMs / 3600_000))} h more` : undefined}
        >
          <div className="kv">
            <div className="kvrow diff">
              <span className="k">When</span>
              <span className="v">{formatWhen(booking.proposedStart)}</span>
            </div>
            <div className="kvrow">
              <span className="k">Guest</span>
              <span className="v">
                {booking.guestName}
                {booking.guestRoom ? ` · ${booking.guestRoom}` : ''}
              </span>
            </div>
            <div className="kvrow">
              <span className="k">Party</span>
              <span className="v">{party}</span>
            </div>
            <div className="kvrow earn">
              <span className="k">Guest pays</span>
              <span className="v">
                <MoneyText money={booking.guestAmount} />
              </span>
            </div>
          </div>
          {proposal?.note && <div className="said">&ldquo;{proposal.note}&rdquo;</div>}
          <p className="hint" style={{ margin: '10px 0 14px' }}>
            Only the time changes — everything else stays as you asked.
          </p>
          <button className="btn" disabled={busy} onClick={accept} style={{ width: '100%' }}>
            Confirm {formatWhen(booking.proposedStart)}
          </button>
        </Panel>
      </div>

      <p className="hint" style={{ marginTop: 20 }}>
        Doesn&rsquo;t work for the guest?{' '}
        <a href="#/bookings/new" className="open">
          Book another supplier instead →
        </a>
      </p>
      {error && <p className="error-line">{error}</p>}
    </div>
  );
}
